import React, { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { Plus, Pencil, Trash2, X, Upload, Sparkles } from "lucide-react";
import VideoEmbed from "../../components/VideoEmbed";

const EMPTY = {
  name: "",
  slug: "",
  short_description: "",
  description: "",
  price: 0,
  currency: "USD",
  duration: "",
  location: "",
  maps_url: "",
  images: [],
  video_url: "",
  featured: false,
  active: true,
  category: "",
  includes: [],
  name_en: "",
  short_description_en: "",
  description_en: "",
  duration_en: "",
  location_en: "",
  category_en: "",
  includes_en: [],
};

export default function AdminExperiences() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = () => api.get("/experiences").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  // Collect existing categories for autocomplete in the modal
  const existingCategories = Array.from(new Set(
    items.flatMap((e) => [e.category, e.category_en]).filter(Boolean)
  )).sort();

  const startNew = () => setEditing({ ...EMPTY });
  const startEdit = (exp) => setEditing({
    ...EMPTY,
    ...exp,
    includes: exp.includes || [],
    includes_en: exp.includes_en || [],
  });

  const save = async () => {
    setErr("");
    try {
      const payload = { ...editing };
      if (typeof payload.includes === "string") {
        payload.includes = payload.includes.split("\n").map((s) => s.trim()).filter(Boolean);
      }
      if (typeof payload.includes_en === "string") {
        payload.includes_en = payload.includes_en.split("\n").map((s) => s.trim()).filter(Boolean);
      }
      payload.price = Number(payload.price);
      if (editing.id) {
        await api.put(`/experiences/${editing.id}`, payload);
      } else {
        await api.post("/experiences", payload);
      }
      setEditing(null);
      load();
    } catch (e) {
      setErr(formatErr(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta experiencia?")) return;
    await api.delete(`/experiences/${id}`);
    load();
  };

  const [optimizing, setOptimizing] = useState(false);
  const optimizeImages = async () => {
    if (!window.confirm("Esto comprime todas las fotos pesadas de tu catálogo para que la página cargue más rápido. ¿Continuar?")) return;
    setOptimizing(true);
    try {
      const r = await api.post("/admin/optimize-images");
      const d = r.data;
      alert(`✅ Optimización lista\n\n• ${d.images_compressed} fotos comprimidas\n• ${d.mb_saved} MB ahorrados\n• ${d.experiences_processed} experiencias actualizadas`);
      load();
    } catch (e) {
      alert("Error: " + formatErr(e));
    } finally {
      setOptimizing(false);
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-experiences">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="bn-eyebrow">Catálogo</div>
          <h1 className="bn-heading text-3xl md:text-4xl mt-2">Experiencias</h1>
          <p className="text-sm mt-2" style={{ color: "var(--bn-text-muted)" }}>Cada campo se edita en ES y EN. Si dejas el EN vacío, se usa el texto en español como respaldo.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={optimizeImages} disabled={optimizing} className="bn-btn-ghost inline-flex items-center gap-2" data-testid="optimize-images-btn">
            {optimizing ? "Optimizando..." : "Optimizar fotos"}
          </button>
          <button onClick={startNew} className="bn-btn-primary inline-flex items-center gap-2" data-testid="new-experience-btn">
            <Plus size={16} /> Nueva experiencia
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {items.map((exp) => (
          <div key={exp.id} className="bn-card overflow-hidden" data-testid={`admin-exp-${exp.id}`}>
            <div className="aspect-video overflow-hidden bg-black relative">
              {exp.images?.[0] ? (
                <img src={exp.images[0]} alt={exp.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-center px-3" style={{ background: "rgba(220,38,38,0.08)", color: "#b91c1c" }}>
                  <div className="bn-eyebrow">⚠ Sin fotos</div>
                  <div className="text-[10px] mt-1 opacity-80">Edita para subir imágenes</div>
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="flex justify-between items-baseline">
                <h3 className="bn-heading text-xl">{exp.name}</h3>
                <span className="bn-heading text-lg">${exp.price} {exp.currency || "USD"}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="bn-eyebrow" style={{ color: "var(--bn-accent)" }}>{exp.category}</div>
                {exp.name_en && <span className="text-[.55rem] px-1.5 py-0.5 rounded" style={{ background: "var(--bn-muted)", color: "var(--bn-text-muted)" }}>EN ✓</span>}
              </div>
              <p className="mt-2 text-sm line-clamp-2" style={{ color: "var(--bn-text-muted)" }}>{exp.short_description}</p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => startEdit(exp)} className="bn-btn-ghost inline-flex items-center gap-2 flex-1 justify-center" data-testid={`edit-${exp.id}`}>
                  <Pencil size={14} /> Editar
                </button>
                <button onClick={() => remove(exp.id)} className="bn-btn-ghost" style={{ color: "#b91c1c", borderColor: "rgba(185,28,28,0.3)" }} data-testid={`delete-${exp.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && <EditorModal editing={editing} setEditing={setEditing} save={save} err={err} existingCategories={existingCategories} />}
    </div>
  );
}

function EditorModal({ editing, setEditing, save, err, existingCategories = [] }) {
  const [tab, setTab] = useState("es"); // es | en
  const [translating, setTranslating] = useState(false);
  const [translateErr, setTranslateErr] = useState("");

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const translateWithAI = async () => {
    setTranslateErr("");
    if (!editing.name) { setTranslateErr("Escribe al menos el nombre en español primero"); return; }
    setTranslating(true);
    try {
      const payload = {
        name: editing.name || "",
        short_description: editing.short_description || "",
        description: editing.description || "",
        duration: editing.duration || "",
        location: editing.location || "",
        category: editing.category || "",
        includes: Array.isArray(editing.includes)
          ? editing.includes
          : (editing.includes || "").split("\n").map((s) => s.trim()).filter(Boolean),
      };
      const res = await api.post("/admin/translate", payload);
      setEditing((e) => ({ ...e, ...res.data }));
      setTab("en");
    } catch (e) {
      setTranslateErr(formatErr(e));
    } finally {
      setTranslating(false);
    }
  };

  const onFiles = async (ev) => {
    const files = Array.from(ev.target.files || []);
    const arr = [...(editing.images || [])];
    for (const f of files) {
      const b64 = await toBase64(f);
      arr.push(b64);
    }
    set("images", arr);
  };

  const removeImg = (i) => {
    const arr = [...editing.images];
    arr.splice(i, 1);
    set("images", arr);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-stretch md:items-center justify-center p-0 md:p-4" style={{ background: "rgba(0,0,0,0.75)" }}>
      <div className="bn-card w-full max-w-3xl md:max-h-[90vh] max-h-[100svh] overflow-auto" style={{ borderRadius: 0 }}>
        <div className="flex items-center justify-between p-6 sticky top-0 z-10" style={{ background: "var(--bn-surface)", borderBottom: "1px solid var(--bn-border)" }}>
          <h2 className="bn-heading text-2xl">{editing.id ? "Editar experiencia" : "Nueva experiencia"}</h2>
          <button onClick={() => setEditing(null)}><X /></button>
        </div>
        <div className="p-6 space-y-5">
          {/* Common fields */}
          <div className="grid md:grid-cols-3 gap-4">
            <Field label={`Precio (${editing.currency || "USD"})`}>
              <input type="number" min="0" step="0.01" className="bn-input" value={editing.price} onChange={(e) => set("price", e.target.value)} data-testid="exp-price" />
            </Field>
            <Field label="Moneda">
              <select className="bn-input" value={editing.currency} onChange={(e) => set("currency", e.target.value)} data-testid="exp-currency">
                <option value="USD">USD — Dólar</option>
                <option value="MXN">MXN — Peso mexicano</option>
                <option value="EUR">EUR — Euro</option>
              </select>
            </Field>
            <Field label="Slug (URL)">
              <input className="bn-input" value={editing.slug || ""} onChange={(e) => set("slug", e.target.value)} placeholder="auto desde el nombre" />
            </Field>
          </div>

          {/* Language tabs */}
          <div className="flex gap-1 items-center justify-between" style={{ borderBottom: "1px solid var(--bn-border)" }}>
            <div className="flex">
              <TabBtn active={tab === "es"} onClick={() => setTab("es")} testid="lang-tab-es">🇲🇽 Español</TabBtn>
              <TabBtn active={tab === "en"} onClick={() => setTab("en")} testid="lang-tab-en">🇺🇸 English {editing.name_en ? "✓" : ""}</TabBtn>
            </div>
            <button
              type="button"
              onClick={translateWithAI}
              disabled={translating}
              className="bn-btn-ghost inline-flex items-center gap-2 mb-1"
              style={{ fontSize: ".7rem", padding: ".5rem .9rem", color: "var(--bn-accent)", borderColor: "var(--bn-accent)", opacity: translating ? 0.6 : 1 }}
              data-testid="translate-ai-btn"
              title="Traduce el texto en español al inglés con IA"
            >
              <Sparkles size={12} /> {translating ? "Traduciendo..." : "Traducir ES → EN con IA"}
            </button>
          </div>
          {translateErr && <div className="text-xs p-2" style={{ background: "rgba(220,38,38,0.1)", color: "#b91c1c", borderRadius: "var(--bn-button-radius)" }}>{translateErr}</div>}

          {tab === "es" && (
            <div className="space-y-4" data-testid="lang-fields-es">
              <Field label="Nombre *">
                <input className="bn-input" value={editing.name} onChange={(e) => set("name", e.target.value)} data-testid="exp-name" />
              </Field>
              <Field label="Categoría">
                <input
                  className="bn-input"
                  value={editing.category}
                  onChange={(e) => set("category", e.target.value)}
                  placeholder="Escribe la categoría que quieras (ej: Familia, Aventura, Spa…)"
                  data-testid="exp-category"
                />
                <div className="text-[.65rem] mt-1.5" style={{ color: "var(--bn-text-muted)" }}>
                  Sugerencias rápidas — toca para usar, o escribe cualquier categoría nueva en el campo.
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="exp-category-suggestions">
                  {["Isla", "Cultural", "Aventura", "Lujo", "Parques", "Beach Club", "Gastronomía", "Bienestar", "Familia", "Romance"]
                    .concat(existingCategories)
                    .filter((v, i, a) => v && a.indexOf(v) === i)
                    .map((c) => (
                      <button
                        type="button"
                        key={c}
                        onClick={() => set("category", c)}
                        className="text-xs px-2.5 py-1 rounded-full border transition-opacity"
                        style={{
                          borderColor: "var(--bn-border)",
                          color: editing.category === c ? "var(--bn-bg)" : "var(--bn-text)",
                          background: editing.category === c ? "var(--bn-accent)" : "transparent",
                        }}
                      >
                        {c}
                      </button>
                    ))}
                </div>
              </Field>
              <Field label="Descripción corta">
                <input className="bn-input" value={editing.short_description} onChange={(e) => set("short_description", e.target.value)} data-testid="exp-short" />
              </Field>
              <Field label="Descripción completa">
                <textarea rows={4} className="bn-input" value={editing.description} onChange={(e) => set("description", e.target.value)} data-testid="exp-description" />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Duración">
                  <input className="bn-input" value={editing.duration} onChange={(e) => set("duration", e.target.value)} placeholder="Ej: Día completo" data-testid="exp-duration" />
                </Field>
                <Field label="Ubicación (texto)">
                  <input className="bn-input" value={editing.location} onChange={(e) => set("location", e.target.value)} placeholder="Ej: Isla Mujeres, Q.R." data-testid="exp-location" />
                </Field>
              </div>
              <Field label="Incluye (una línea por ítem)">
                <textarea rows={4} className="bn-input" value={Array.isArray(editing.includes) ? editing.includes.join("\n") : editing.includes} onChange={(e) => set("includes", e.target.value)} data-testid="exp-includes" />
              </Field>
            </div>
          )}

          {tab === "en" && (
            <div className="space-y-4" data-testid="lang-fields-en">
              <div className="text-xs p-3" style={{ background: "var(--bn-muted)", borderRadius: "var(--bn-button-radius)", color: "var(--bn-text-muted)" }}>
                Estos campos son opcionales. Si los dejas vacíos, la versión en inglés mostrará el texto en español como fallback.
              </div>
              <Field label="Name">
                <input className="bn-input" value={editing.name_en || ""} onChange={(e) => set("name_en", e.target.value)} data-testid="exp-name-en" />
              </Field>
              <Field label="Category">
                <input
                  className="bn-input"
                  value={editing.category_en || ""}
                  onChange={(e) => set("category_en", e.target.value)}
                  placeholder="Type any category (e.g. Family, Adventure, Spa…)"
                  data-testid="exp-category-en"
                />
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="exp-category-en-suggestions">
                  {["Island", "Cultural", "Adventure", "Luxury", "Parks", "Beach Club", "Food", "Wellness", "Family", "Romance"]
                    .concat(existingCategories)
                    .filter((v, i, a) => v && a.indexOf(v) === i)
                    .map((c) => (
                      <button
                        type="button"
                        key={c}
                        onClick={() => set("category_en", c)}
                        className="text-xs px-2.5 py-1 rounded-full border transition-opacity"
                        style={{
                          borderColor: "var(--bn-border)",
                          color: editing.category_en === c ? "var(--bn-bg)" : "var(--bn-text)",
                          background: editing.category_en === c ? "var(--bn-accent)" : "transparent",
                        }}
                      >
                        {c}
                      </button>
                    ))}
                </div>
              </Field>
              <Field label="Short description">
                <input className="bn-input" value={editing.short_description_en || ""} onChange={(e) => set("short_description_en", e.target.value)} data-testid="exp-short-en" />
              </Field>
              <Field label="Full description">
                <textarea rows={4} className="bn-input" value={editing.description_en || ""} onChange={(e) => set("description_en", e.target.value)} data-testid="exp-description-en" />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Duration">
                  <input className="bn-input" value={editing.duration_en || ""} onChange={(e) => set("duration_en", e.target.value)} placeholder="e.g. Full day" data-testid="exp-duration-en" />
                </Field>
                <Field label="Location">
                  <input className="bn-input" value={editing.location_en || ""} onChange={(e) => set("location_en", e.target.value)} data-testid="exp-location-en" />
                </Field>
              </div>
              <Field label="Includes (one per line)">
                <textarea rows={4} className="bn-input" value={Array.isArray(editing.includes_en) ? editing.includes_en.join("\n") : (editing.includes_en || "")} onChange={(e) => set("includes_en", e.target.value)} data-testid="exp-includes-en" />
              </Field>
            </div>
          )}

          {/* Images & media - shared */}
          <div className="pt-4 mt-4" style={{ borderTop: "1px solid var(--bn-border)" }}>
            <div className="bn-eyebrow mb-4">Medios (compartidos ES/EN)</div>

            <Field label="Google Maps URL (opcional)">
              <input className="bn-input" value={editing.maps_url} onChange={(e) => set("maps_url", e.target.value)} placeholder="https://maps.google.com/..." data-testid="exp-maps" />
            </Field>

            <div className="mt-4">
              <Field label="Galería de imágenes">
                <div className="flex flex-wrap gap-3">
                  {(editing.images || []).map((src, i) => (
                    <div key={i} className="relative group w-24 h-24 overflow-hidden rounded" style={{ border: "1px solid var(--bn-border)" }}>
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => removeImg(i)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  <label className="w-24 h-24 flex flex-col items-center justify-center cursor-pointer" style={{ border: "1px dashed var(--bn-border)", borderRadius: "var(--bn-button-radius)" }}>
                    <Upload size={18} />
                    <span className="text-[.6rem] mt-1 bn-eyebrow">Subir</span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="exp-upload-images" />
                  </label>
                </div>
                <textarea rows={2} className="bn-input mt-2" placeholder="Pega URLs de imágenes, una por línea" onBlur={(e) => {
                  const extra = e.target.value.split("\n").map((s) => s.trim()).filter(Boolean);
                  if (extra.length) {
                    set("images", [...(editing.images || []), ...extra]);
                    e.target.value = "";
                  }
                }} data-testid="exp-image-urls" />
              </Field>
            </div>

            <div className="mt-4">
              <Field label="Video YouTube / Vimeo URL">
                <input className="bn-input" value={editing.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://youtube.com/..." data-testid="exp-video" />
                {editing.video_url && <div className="mt-3"><VideoEmbed url={editing.video_url} /></div>}
              </Field>
            </div>
          </div>

          <div className="flex items-center gap-5 pt-4" style={{ borderTop: "1px solid var(--bn-border)" }}>
            <label className="flex items-center gap-2 bn-body-font">
              <input type="checkbox" checked={editing.featured} onChange={(e) => set("featured", e.target.checked)} /> Destacada
            </label>
            <label className="flex items-center gap-2 bn-body-font">
              <input type="checkbox" checked={editing.active} onChange={(e) => set("active", e.target.checked)} /> Activa
            </label>
          </div>

          {err && <div className="text-sm p-3" style={{ background: "rgba(220,38,38,0.1)", color: "#b91c1c" }}>{err}</div>}

          <div className="flex gap-3 pt-4 sticky bottom-0 py-4 -mx-6 px-6" style={{ background: "var(--bn-surface)", borderTop: "1px solid var(--bn-border)" }}>
            <button onClick={save} className="bn-btn-primary" data-testid="save-exp">Guardar</button>
            <button onClick={() => setEditing(null)} className="bn-btn-ghost">Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className="bn-eyebrow px-5 py-3 -mb-px transition-all"
      style={{
        borderBottom: active ? "2px solid var(--bn-accent)" : "2px solid transparent",
        color: active ? "var(--bn-accent)" : "var(--bn-text-muted)",
      }}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="bn-eyebrow block mb-2">{label}</span>
      {children}
    </label>
  );
}

/**
 * Convert a File to a compressed base64 JPEG dataURL.
 * - Resizes to max 1600px on the longest side
 * - JPEG quality 0.82 (good visual quality, small payload)
 * - Strips EXIF metadata
 * Typical 3-5MB iPhone photo → ~200-400KB. Drastically faster + reliable upload.
 */
function toBase64(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("No file"));
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        try {
          const MAX = 1600;
          let { width, height } = img;
          if (width > MAX || height > MAX) {
            if (width >= height) {
              height = Math.round((height * MAX) / width);
              width = MAX;
            } else {
              width = Math.round((width * MAX) / height);
              height = MAX;
            }
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          // Quality 0.82 — sweet spot for travel photography
          const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
          resolve(dataUrl);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error("Image decode failed"));
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}
